import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { ChevronLeft, ChevronRight, KeyboardArrowUp } from "@mui/icons-material";
import { tokens } from "../../../theme";
import { useRef, useState } from "react";
import MemoryBookReassignModal from "./MemoryBookAssignModal";
import { MemoryBookEntry } from "./MemoryBookCard";
import MemoryBookMinimisedPill from "./MemoryBookMinimisedPill";
import StatusColumn from "./StatusColumn";

type MemoryBookStatus = "overdue" | "in_danger" | "safe";

interface StaffAssignment {
  id: number;
  avatarUrl?: string;
  name: string;
  assignedCount: number;
}

interface MemoryBookSummaryProps {
  entries?: MemoryBookEntry[];
  staffAssignments?: StaffAssignment[];
  statusCounts?: { inDanger: number; almostDue: number; safe: number };
}

const MOCK_ENTRIES: MemoryBookEntry[] = [
  { id: 1, deceasedName: "Margaret Chari",    timeInfo: "5 Days ago",     commentCount: 31,  progressPercent: 85, status: "overdue"   },
  { id: 2, deceasedName: "Tawanda Moyo",      timeInfo: "4 Days ago",     commentCount: 94,  progressPercent: 60, status: "overdue"   },
  { id: 3, deceasedName: "Ruth Ndlovu",       timeInfo: "3 Days ago",     commentCount: 124, progressPercent: 40, status: "overdue"   },
  { id: 4, deceasedName: "Peter Gumbo",       timeInfo: "Today, 09:00",   commentCount: 34,  progressPercent: 55, status: "in_danger" },
  { id: 5, deceasedName: "Grace Mutasa",      timeInfo: "Today, 13:30",   commentCount: 34,  progressPercent: 45, status: "in_danger" },
  { id: 6, deceasedName: "Joseph Nyathi",     timeInfo: "Today, 14:00",   commentCount: 52,  progressPercent: 30, status: "in_danger" },
  { id: 7, deceasedName: "Agnes Sibanda",     timeInfo: "12 May, 10:00",  commentCount: 304, progressPercent: 90, status: "safe"      },
  { id: 8, deceasedName: "Daniel Mutsvangwa", timeInfo: "13 May, 11:30",  commentCount: 14,  progressPercent: 75, status: "safe"      },
  { id: 9, deceasedName: "Elizabeth Zhou",    timeInfo: "14 May, 15:00",  commentCount: 934, progressPercent: 95, status: "safe"      },
];

const MOCK_STAFF: StaffAssignment[] = [
  { id: 1, name: "Tapiwa Chikwana", assignedCount: 3 },
  { id: 2, name: "Foster Jumbe",    assignedCount: 1 },
  { id: 3, name: "King Stone",      assignedCount: 0 },
  { id: 4, name: "John Chikoko",    assignedCount: 0 },
];

const MemoryBookSummary = ({
  entries = MOCK_ENTRIES,
  staffAssignments = MOCK_STAFF,
  statusCounts,
}: MemoryBookSummaryProps) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [expanded, setExpanded] = useState(true);
  const [modalEntry, setModalEntry] = useState<MemoryBookEntry | null>(null);
  const [currentAssigneeId, setCurrentAssigneeId] = useState<number>(1);

  const overdueCount   = entries.filter((e) => e.status === "overdue").length;
  const inDangerCount  = entries.filter((e) => e.status === "in_danger").length;

  const computedCounts = {
    inDanger:  inDangerCount,
    almostDue: overdueCount,
    safe:      entries.filter((e) => e.status === "safe").length,
    ...(statusCounts ?? {}),
  };

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir === "left" ? -310 : 310, behavior: "smooth" });
  };

  const grouped = {
    overdue:   entries.filter((e) => e.status === "overdue"),
    in_danger: entries.filter((e) => e.status === "in_danger"),
    safe:      entries.filter((e) => e.status === "safe"),
  };

  if (!expanded) {
    return (
      <MemoryBookMinimisedPill
        overdueCount={overdueCount}
        inDangerCount={inDangerCount}
        onExpand={() => setExpanded(true)}
      />
    );
  }
  return (
    <>
      <Box
        sx={{
          position: "relative",
        //   maxWidth: { xs: "95%", md: "85%" },
            width: "fit-content",
          //mt: "-410px",
          mx: "auto",
          mb: { xs: 4, md: 6 },
        //   px: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            border: `1px solid ${colors.primary[300]}30`,
            borderRadius: "40px",
            p: { xs: 2, md: 3 },
            backgroundColor: colors.primary[900],
            position: "relative",
          }}
        >
          <Tooltip title="Minimise" arrow>
            <IconButton
              onClick={() => setExpanded(false)}
              size="small"
              sx={{
                position: "absolute",
                top: 12,
                right: 16,
                color: colors.primary[700],
                zIndex: 3,
                "&:hover": { color: colors.primary[400] },
              }}
            >
              <KeyboardArrowUp sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          <IconButton
            onClick={() => scroll("left")}
            sx={{
              position: "absolute",
              left: { xs: -20, md: -28 },
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: colors.primary[400],
              color: "white",
              width: { xs: 32, md: 40 },
              height: { xs: 32, md: 40 },
              zIndex: 2,
              "&:hover": { backgroundColor: colors.primary[300] },
              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            }}
          >
            <ChevronLeft />
          </IconButton>

          <IconButton
            onClick={() => scroll("right")}
            sx={{
              position: "absolute",
              right: { xs: -20, md: -28 },
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: colors.primary[400],
              color: "white",
              width: { xs: 32, md: 40 },
              height: { xs: 32, md: 40 },
              zIndex: 2,
              "&:hover": { backgroundColor: colors.primary[300] },
              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            }}
          >
            <ChevronRight />
          </IconButton>

          <Box
            ref={scrollRef}
            sx={{
              display: "flex",
              gap: 2,
              overflowX: "auto",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Box
              sx={{
                minWidth: { xs: 240, sm: 260 },
                maxWidth: { xs: 240, sm: 260 },
                backgroundColor: colors.primary[800],
                borderRadius: "30px",
                p: 2.5,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  letterSpacing: "0.1em",
                  color: colors.primary[700],
                }}
              >
                MEMORY BOOKS SUMMARY
              </Typography>

              {/* Status legend */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  mb: 2,
                }}
              >
                {[
                  {
                    label: "In danger",
                    count: computedCounts.inDanger,
                    color: "#E11D48",
                  },
                  {
                    label: "Almost due",
                    count: computedCounts.almostDue,
                    color: "#f59e0b",
                  },
                  {
                    label: "SAFE",
                    count: computedCounts.safe,
                    color: "#10b981",
                  },
                ].map(({ label, count, color }) => (
                  <Box
                    key={label}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{ fontSize: "0.8rem", color: colors.primary[100] }}
                      >
                        {label}
                      </Typography>
                    </Box>
                    <Chip
                      label={count}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        backgroundColor: colors.primary[700] + "30",
                        color: colors.primary[100],
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </Box>
                ))}
              </Box>

              {/* Staff assignments */}
              <Box
                sx={{
                  borderRadius: "20px",
                  backgroundColor: colors.primary[900],
                  px: 1.5,
                  py: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: colors.primary[700],
                    mb: 1.5,
                  }}
                >
                  Memory book assignments
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {staffAssignments.map((staff) => (
                    <Tooltip
                      key={staff.id}
                      title={`${staff.name}: ${staff.assignedCount} assigned`}
                      arrow
                    >
                      <Box sx={{ position: "relative", cursor: "pointer" }}>
                        <Avatar
                          src={staff.avatarUrl}
                          sx={{
                            width: 40,
                            height: 40,
                            border: `2px solid ${colors.primary[400]}`,
                          }}
                        >
                          {staff.name[0]}
                        </Avatar>
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: -4,
                            right: -4,
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            backgroundColor: "#E11D48",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: `1.5px solid ${colors.primary[900]}`,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "white",
                              fontWeight: 700,
                              lineHeight: 1,
                            }}
                          >
                            {staff.assignedCount}
                          </Typography>
                        </Box>
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Status columns */}
            {(["overdue", "in_danger", "safe"] as MemoryBookStatus[]).map(
              (status) => (
                <StatusColumn
                  key={status}
                  status={status}
                  entries={grouped[status]}
                  colors={colors}
                  onCardClick={(entry) => setModalEntry(entry)}
                />
              ),
            )}
          </Box>
        </Box>
      </Box>

      <MemoryBookReassignModal
        open={!!modalEntry}
        onClose={() => setModalEntry(null)}
        entry={modalEntry}
        staffOptions={staffAssignments}
        currentAssigneeId={currentAssigneeId}
        onReassign={(entryId, staffId) => {
          setCurrentAssigneeId(staffId);
          setModalEntry(null);
        }}
        onViewBook={() => {}}
      />
    </>
  );
};

export default MemoryBookSummary;